import { computed, inject, Injectable, linkedSignal, resource, Signal } from '@angular/core';

import { InsightsType } from '../../../../customer/model/insights.model';
import { FeatureVisibilityService } from '../../../../customer/services/feature-visibility.service';
import { CdhTwinService } from '../../../../insights/clients/cdh-twin/cdh-twin.service';
import { InsightsViewModel } from '../model/insights.view-model';

/**
 * Provides a composable which encapsules the customer insights logic.
 */
@Injectable({ providedIn: 'root' })
export class CustomerInsightsComposable {
  private readonly cdhTwinService = inject(CdhTwinService);
  private readonly featureVisibilityService = inject(FeatureVisibilityService);

  use(gcid: Signal<string | undefined>) {
    const enabled = computed(() => !this.featureVisibilityService.isHidden(InsightsType.CUSTOMER_INSIGHTS));

    const insightsResource = resource({
      params: computed(() => (enabled() ? gcid() : undefined)),
      loader: async ({ params: gcid }) => this.cdhTwinService.getCustomerInsights(gcid),
    });

    const insightsInDefinedRanges = computed(() => {
      const insights = insightsResource.value();
      if (insights === undefined) {
        return undefined;
      }
      return {
        customerValueClass: valueInDefinedRange(insights.customerValueClass, 0, 5),
        purchaseScoreClass: {
          bmw: valueInDefinedRange(insights.purchaseScoreClass?.bmw, 1, 20),
          mini: valueInDefinedRange(insights.purchaseScoreClass?.mini, 1, 20),
        },
        bevScoreClass: valueInDefinedRange(insights.bevScoreClass, 1, 20),
        nextBestVehicles: insights.nextBestVehicles,
        updateDate: insights.updateDate,
        marketGcid: insights.marketGcid,
      };
    });

    const brandSelection = linkedSignal<'BMW' | 'MINI'>(() => {
      const value = insightsInDefinedRanges();
      const purchaseScoreClassBmw = value?.purchaseScoreClass?.bmw;
      const purchaseScoreClassMini = value?.purchaseScoreClass?.mini;

      // Primary: pick the brand with the higher purchase score
      if ((purchaseScoreClassBmw ?? -Infinity) < (purchaseScoreClassMini ?? -Infinity)) {
        return 'MINI';
      }
      if ((purchaseScoreClassMini ?? -Infinity) < (purchaseScoreClassBmw ?? -Infinity)) {
        return 'BMW';
      }

      // Tie-breaker: if scores are equal or both missing, prefer the brand that has NBVs
      const bmwHasNbv = (value?.nextBestVehicles?.bmw ?? []).length > 0;
      const miniHasNbv = (value?.nextBestVehicles?.mini ?? []).length > 0;
      if (!bmwHasNbv && miniHasNbv) {
        return 'MINI';
      }

      // Default
      return 'BMW';
    });

    const loading = computed(() => insightsResource.isLoading());

    const insightViewModel: Signal<InsightsViewModel | undefined> = computed(() => {
      const insights = insightsInDefinedRanges();
      let model: InsightsViewModel;
      if (brandSelection() === 'BMW') {
        model = {
          customerValueClass: insights?.customerValueClass,
          purchaseScoreClass: insights?.purchaseScoreClass?.bmw,
          bevScoreClass: insights?.bevScoreClass,
          nextBestVehicles: insights?.nextBestVehicles?.bmw ?? [],
          selectionDisabled:
            insights?.purchaseScoreClass?.mini == undefined && (insights?.nextBestVehicles?.mini ?? []).length === 0,
        };
      } else {
        model = {
          customerValueClass: insights?.customerValueClass,
          purchaseScoreClass: insights?.purchaseScoreClass?.mini,
          bevScoreClass: insights?.bevScoreClass,
          nextBestVehicles: insights?.nextBestVehicles?.mini ?? [],
          selectionDisabled:
            insights?.purchaseScoreClass?.bmw == undefined && (insights?.nextBestVehicles?.bmw ?? []).length === 0,
        };
      }
      if (
        model.customerValueClass == undefined &&
        model.purchaseScoreClass == undefined &&
        model.bevScoreClass == undefined &&
        model.nextBestVehicles.length === 0
      ) {
        return undefined;
      }
      return model;
    });

    function valueInDefinedRange(value: number | undefined, min: number, max: number) {
      if (value === undefined || value < min || value > max) {
        return undefined;
      } else {
        return value;
      }
    }

    return {
      insightViewModel,
      loading,
      brandSelection,
      enabled,
    };
  }
}

export type UseCustomerInsights = ReturnType<CustomerInsightsComposable['use']>;
